import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Settings as SettingsIcon,
  Mail,
  Truck,
  Image as ImageIcon,
  Save,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flags, setFlags] = useState({ branded_emails: false, public_order_tracking: false });
  const [brand, setBrand] = useState({
    logo_url: '',
    brand_color: '#0ea5e9',
    business_name: '',
    business_address: '',
    support_email: '',
    website_url: '',
  });
  const [initial, setInitial] = useState({ flags: null, brand: null });
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/settings`, { headers: auth() });
      setFlags(res.data.flags || {});
      setBrand(res.data.brand || {});
      setInitial({ flags: res.data.flags, brand: res.data.brand });
    } catch (e) {
      toast.error('Failed to load settings');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/media/list`, { headers: auth() });
      setMediaItems(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const hasFlagChanges = initial.flags && JSON.stringify(flags) !== JSON.stringify(initial.flags);
  const hasBrandChanges = initial.brand && JSON.stringify(brand) !== JSON.stringify(initial.brand);
  const hasChanges = hasFlagChanges || hasBrandChanges;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (hasFlagChanges) payload.flags = flags;
      if (hasBrandChanges) payload.brand = brand;
      const res = await axios.patch(`${API_URL}/api/admin/settings`, payload, { headers: auth() });
      setFlags(res.data.flags);
      setBrand(res.data.brand);
      setInitial({ flags: res.data.flags, brand: res.data.brand });
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fullMediaUrl = (item) => item.relative_url ? `${API_URL}${item.relative_url}` : item.url;

  if (loading) {
    return <div className="p-8 text-gray-400">Loading settings...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <SettingsIcon className="w-7 h-7 text-blue-400" /> Settings
          </h1>
          <p className="text-gray-400 text-sm">
            Feature flags, branding, and business info used across emails and public pages.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={!hasChanges || saving}
          className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      {/* Feature Flags */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-400" />
            Feature Flags
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <FlagRow
            testid="flag-branded-emails"
            icon={<Mail className="w-5 h-5 text-blue-400" />}
            title="Branded Emails"
            description="Replace legacy transactional emails with the new branded templates (logo, sky-blue accents, business address in footer). Affects order confirmation, status updates, and refund emails."
            value={flags.branded_emails}
            onChange={(v) => setFlags({ ...flags, branded_emails: v })}
          />
          <FlagRow
            testid="flag-public-tracking"
            icon={<Truck className="w-5 h-5 text-blue-400" />}
            title="Public Order Tracking"
            description="Enables the /track/{token} page so customers can check order status and tracking without logging in. When OFF, tracking links in shipped emails are hidden and the public page returns 404."
            value={flags.public_order_tracking}
            onChange={(v) => setFlags({ ...flags, public_order_tracking: v })}
          />
        </CardContent>
      </Card>

      {/* Brand Identity */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-400" />
            Brand Identity
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">Used in branded email headers/footers and the public tracking page.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Logo */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Email / Tracking Logo</label>
            <div className="flex items-start gap-4">
              <div className="w-40 h-20 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-700">
                {brand.logo_url ? (
                  <img src={brand.logo_url} alt="Brand logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={brand.logo_url || ''}
                  onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })}
                  placeholder="https://…/logo.png"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                  data-testid="brand-logo-url-input"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setMediaPickerOpen(true); loadMedia(); }}
                    className="text-xs"
                    data-testid="pick-from-media-btn"
                  >
                    Pick from Media Library
                  </Button>
                  {brand.logo_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setBrand({ ...brand, logo_url: '' })}
                      className="text-xs"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Brand color (hex)"
              value={brand.brand_color || ''}
              onChange={(v) => setBrand({ ...brand, brand_color: v })}
              testid="brand-color-input"
            />
            <TextField
              label="Website URL"
              value={brand.website_url || ''}
              onChange={(v) => setBrand({ ...brand, website_url: v })}
              testid="brand-website-input"
            />
            <TextField
              label="Business name"
              value={brand.business_name || ''}
              onChange={(v) => setBrand({ ...brand, business_name: v })}
              testid="brand-business-name-input"
            />
            <TextField
              label="Support email"
              value={brand.support_email || ''}
              onChange={(v) => setBrand({ ...brand, support_email: v })}
              testid="brand-support-email-input"
            />
            <div className="md:col-span-2">
              <TextField
                label="Business address (shown in email footer)"
                value={brand.business_address || ''}
                onChange={(v) => setBrand({ ...brand, business_address: v })}
                testid="brand-address-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Media picker modal */}
      {mediaPickerOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="media-picker-modal">
          <Card className="bg-gray-800 border-gray-700 max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Pick a logo from Media Library</CardTitle>
              <button onClick={() => setMediaPickerOpen(false)} className="text-gray-400 hover:text-white">✕</button>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              {mediaItems.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No media yet. Upload images in the Media page first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {mediaItems.map((m) => {
                    const url = fullMediaUrl(m);
                    const selected = url === brand.logo_url;
                    return (
                      <button
                        key={m.id}
                        onClick={() => { setBrand({ ...brand, logo_url: url }); setMediaPickerOpen(false); }}
                        className={`relative bg-white rounded-lg p-2 border-2 transition hover:scale-[1.02] ${
                          selected ? 'border-blue-500' : 'border-gray-700'
                        }`}
                        data-testid={`media-item-${m.id}`}
                      >
                        <img src={url} alt={m.original_name} className="w-full h-24 object-contain" />
                        <span className="block mt-1 text-[10px] truncate text-gray-700">{m.original_name}</span>
                        {selected && <CheckCircle2 className="absolute top-1 right-1 w-4 h-4 text-blue-500 bg-white rounded-full" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const FlagRow = ({ icon, title, description, value, onChange, testid }) => (
  <div className="flex items-start gap-4 bg-gray-900/50 rounded-lg p-4 border border-gray-700">
    <div className="mt-1">{icon}</div>
    <div className="flex-1">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-white font-semibold">{title}</h3>
        <Switch
          checked={value}
          onCheckedChange={onChange}
          data-testid={testid}
        />
      </div>
      <p className="text-gray-400 text-sm mt-1 leading-relaxed">{description}</p>
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium">
        <span className={`px-2 py-0.5 rounded ${value ? 'bg-green-500/15 text-green-300 border border-green-500/40' : 'bg-gray-700/40 text-gray-400 border border-gray-700'}`}>
          {value ? 'ON' : 'OFF'}
        </span>
      </div>
    </div>
  </div>
);

const TextField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-sm text-gray-400 mb-1.5">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
      data-testid={testid}
    />
  </div>
);

export default AdminSettings;
